// Brand colors
export const brand = {
  deep: "#0A0C1B",
  aqua: "#00FFFF",
  violet: "#6C63FF",
  amber: "#FFAA33",
  paper: "#F5F5F5",
  white: "#FFFFFF",
  black: "#000000",
  green: "#00FFAA",
} as const;

// Copy text
export const Copy = {
  title: "SUZAN AI CYBERSECURITY WHITEPAPER",
  subtitle: "Illustration made by Suzan AI",
} as const;

// Deterministic anonymization: generate original random-like labels from keys
function hashStr(s: string): number {
  let h = 2166136261 >>> 0; // FNV-1a base
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function randomLabelFromKey(key: string): string {
  const adjectives = [
    "Astral","Crimson","Nebula","Quantum","Aurora","Obsidian","Prismatic","Velvet","Verdant","Ivory",
    "Cobalt","Saffron","Indigo","Lunar","Solar","Glacial","Cerulean","Amber","Violet","Copper",
    "Onyx","Silver","Golden","Teal","Scarlet","Azure","Umber","Emerald","Ruby","Topaz",
    "Nimbus","Echo","Arctic","Boreal"
  ];
  const nouns = [
    "Weave","Lattice","Cascade","Pulse","Cipher","Array","Matrix","Drift","Halo","Spectra",
    "Orbit","Glyph","Vertex","Fractal","Vector","Nova","Flux","Prism","Spiral","Circuit",
    "Mesh","Monolith","Beacon","Arc","Vortex","Aurora","Field","Forge","Canopy","Tessella"
  ];
  const h = hashStr(key);
  const adj = adjectives[h % adjectives.length];
  const noun = nouns[(h >>> 5) % nouns.length];
  return `${adj} ${noun}`;
}

// Source list of modes (keys + original labels). We keep keys stable and re-label below.
const originalModes: [string, string][] = [
  ["convex", "Convex"],
  ["concave", "Concave"],
  ["checker", "Checker Bulge"],
  ["spiral", "Spiral Field"],
  ["moire", "Moire Grid"],
  ["diamond", "Diamond Warp"],
  ["iso", "Iso Cubes"],
  ["shield", "Shield"],
  ["shieldHoney", "Shield Honeycomb"],
  ["lock", "Padlock"],
  ["radar", "Radar"],
  ["mesh", "Zero‑Trust Mesh"],
  ["circuit", "Circuit Traces"],
  ["codeRain", "Code Rain"],
  ["fingerprint", "Fingerprint"],
  ["firewall", "Firewall"],
  ["agamBars", "Agam Bars"],
  ["agamogram", "Agamogram"],
  ["agamPrism", "Agam Prism"],
  ["agamWeave", "Agam Weave"],
  ["calderHowe", "Calder × Howe"],
  ["tinguelyJansen", "Tinguely × Jansen"],
  ["tinguely", "Tinguely Machine"],
  ["tinguelyGears", "Tinguely Gears"],
  ["tinguelyWheels", "Tinguely Wheels"],
  ["jansen", "Jansen Strandbeest"],
  ["jansenLegs", "Jansen Linkage"],
  ["jansenWind", "Jansen Wind"],
  ["grandma", "Grand‑Ma"],
  ["vasarelyMondrian", "Vasarely × Mondrian"],
  ["vasarelyLeCorbusier", "Vasarely × Le Corbusier"],
  ["vasarelyBauhaus", "Vasarely × Bauhaus"],
  ["vasarelyKandinsky", "Vasarely × Kandinsky"],
  ["vasarelyEames", "Vasarely × Eames"],
  ["vasarelyMies", "Vasarely × Mies"],
  ["vasarelyFoster", "Vasarely × Foster"],
  ["vasarelySolLewitt", "Vasarely × Sol LeWitt"],
  ["vasarelyMolnar", "Vasarely × Molnár"],
  ["vasarelyCaseyReas", "Vasarely × Casey Reas"],
  ["vasarelyAnadol", "Vasarely × Anadol"],
  ["vasarelyMohr", "Vasarely × Mohr"],
  ["vasarelyAlbers", "Vasarely × Albers"],
  ["vasarelyRiley", "Vasarely × Riley"],
  ["vasarelyEscher", "Vasarely × Escher"],
  ["vasarelyTurrell", "Vasarely × Turrell"],
  ["vasarelyCalder", "Vasarely × Calder"],
  ["vasarelyHowe", "Vasarely × Howe"],
  ["vasarelyAgam", "Vasarely × Agam"],
  ["vasarelyTuring", "Vasarely × Turing"],
  ["vasarelySuzan", "Vasarely × Suzan AI"],
  ["vasarelyDaVinci", "Vasarely × Da Vinci"],
  ["vasarelyMichelangelo", "Vasarely × Michelangelo"],
  ["vasarelyRaphael", "Vasarely × Raphael"],
  ["vasarelyVelazquez", "Vasarely × Velázquez"],
  ["vasarelyIngres", "Vasarely × Ingres"],
  ["vasarelyCaravaggio", "Vasarely × Caravaggio"],
  ["vasarelyRembrandt", "Vasarely × Rembrandt"],
  ["vasarelyVermeer", "Vasarely × Vermeer"],
  ["vasarelyGoya", "Vasarely × Goya"],
  ["vasarelyCourbet", "Vasarely × Courbet"],
  ["vasarelyTurner", "Vasarely × Turner"],
  ["vasarelyDelacroix", "Vasarely × Delacroix"],
  ["vasarelyMonet", "Vasarely × Monet"],
  ["vasarelyVanGogh", "Vasarely × Van Gogh"],
  ["vasarelyDegas", "Vasarely × Degas"],
  ["vasarelyCezanne", "Vasarely × Cezanne"],
  ["vasarelySeurat", "Vasarely × Seurat"],
  ["vasarelyMatisse", "Vasarely × Matisse"],
  ["vasarelyPicasso", "Vasarely × Picasso"],
  ["vasarelyDali", "Vasarely × Dali"],
  ["vasarelyHermes", "Vasarely × Hermès"],
  ["vasarelyChanel", "Vasarely × Chanel"],
  ["vasarelyDior", "Vasarely × Dior"],
  ["vasarelyLouisVuitton", "Vasarely × Louis Vuitton"],
  ["vasarelyCartier", "Vasarely × Cartier"],
  ["vasarelyVancleef", "Vasarely × Van Cleef & Arpels"],
  ["vasarelyRolex", "Vasarely × Rolex"],
  ["vasarelyPrada", "Vasarely × Prada"],
  ["vasarelyGivenchy", "Vasarely × Givenchy"],
  ["vasarelyYsl", "Vasarely × Yves Saint Laurent"],
  ["vasarelyBalenciaga", "Vasarely × Balenciaga"],
  ["vasarelyMargiela", "Vasarely × Maison Margiela"],
  ["vasarelyIssey", "Vasarely × Issey Miyake"],
  ["vasarelyTesla", "Vasarely × Tesla"],
  ["vasarelyApple", "Vasarely × Apple"],
  ["vasarelyTagheuer", "Vasarely × Tag Heuer"],
  ["vasarelyMontblanc", "Vasarely × Montblanc"],
  ["vasarelyRimowa", "Vasarely × Rimowa"],
  ["vasarelyRichardmille", "Vasarely × Richard Mille"],
  ["vasarelyRollsroyce", "Vasarely × Rolls-Royce"],
  ["vasarelyBo", "Vasarely × Bang & Olufsen"],
  ["vasarelyLeica", "Vasarely × Leica"],
  ["vasarelyBottega", "Vasarely × Bottega Veneta"],
  ["vasarelyMoncler", "Vasarely × Moncler"],
  ["vasarelySuzanLuxury", "Vasarely × Suzan AI"],
  ["vasarelyAirbus", "Vasarely × Airbus"],
  ["vasarelySafran", "Vasarely × Safran"],
  ["vasarelyDassaultsystemes", "Vasarely × Dassault Systèmes"],
  ["vasarelyThales", "Vasarely × Thales"],
  ["vasarelyCapgemini", "Vasarely × Capgemini"],
  ["vasarelyAtos", "Vasarely × Atos"],
  ["vasarelyOrange", "Vasarely × Orange"],
  ["vasarelyBouygues", "Vasarely × Bouygues"],
  ["vasarelyVinci", "Vasarely × Vinci"],
  ["vasarelyTotalenergies", "Vasarely × TotalEnergies"],
  ["vasarelyEngie", "Vasarely × Engie"],
  ["vasarelyEdf", "Vasarely × EDF"],
  ["vasarelyLvmh", "Vasarely × LVMH"],
  ["vasarelyLoreal", "Vasarely × L'Oréal"],
  ["vasarelySanofi", "Vasarely × Sanofi"],
  ["vasarelyDanone", "Vasarely × Danone"],
  ["vasarelyVeolia", "Vasarely × Veolia"],
  ["vasarelyBnp", "Vasarely × BNP Paribas"],
  ["vasarelySocgen", "Vasarely × Société Générale"],
  ["vasarelyAxa", "Vasarely × AXA"],
  ["vasarelyCreditagricole", "Vasarely × Crédit Agricole"],
  ["vasarelyVivendi", "Vasarely × Vivendi"],
  ["vasarelyPublicis", "Vasarely × Publicis"],
  ["vasarelyRenault", "Vasarely × Renault"],
  ["vasarelyStellantis", "Vasarely × Stellantis"],
  ["vasarelyMichelin", "Vasarely × Michelin"],
  ["vasarelyAirliquide", "Vasarely × Air Liquide"],
  ["vasarelyKering", "Vasarely × Kering"],
  ["vasarelyCern", "Vasarely × CERN"],
  ["vasarelyDwave", "Vasarely × D-Wave"],
  ["vasarelyNasajpl", "Vasarely × NASA JPL"],
  ["vasarelyGpt5", "Vasarely × GPT-5"],
  ["vasarelyDeepmind", "Vasarely × DeepMind"],
  ["vasarelyOpenaisafety", "Vasarely × OpenAI Safety"],
  ["vasarelyIpcc", "Vasarely × IPCC"],
  ["vasarelyOceancleanup", "Vasarely × The Ocean Cleanup"],
  ["vasarelyEsagaia", "Vasarely × ESA GAIA"],
  ["vasarelyCrispr", "Vasarely × CRISPR Lab"],
  ["vasarelyHpp", "Vasarely × Human Proteome Project"],
  ["vasarelyPasteur", "Vasarely × Institut Pasteur"],
  ["vasarelyBrianeno", "Vasarely × Brian Eno"],
  ["vasarelyNeuralink", "Vasarely × Neuralink"],
  ["vasarelyIrcam", "Vasarely × IRCAM"],
  ["vasarelyUnescoaiethics", "Vasarely × UNESCO AI Ethics"],
  ["vasarelyWorldbank", "Vasarely × World Bank"],
  ["vasarelyBlockchaingovernance", "Vasarely × Blockchain Governance"],
  ["vasarelyWho", "Vasarely × WHO"],
  ["vasarelyUnicef", "Vasarely × UNICEF"],
  ["vasarelyApc", "Vasarely × A.P.C."],
  ["vasarelyKitsune", "Vasarely × Maison Kitsuné"],
  ["vasarelyAmi", "Vasarely × AMI Paris"],
  ["vasarelyJacquemus", "Vasarely × Jacquemus"],
  ["vasarelySezane", "Vasarely × Sézane"],
  ["vasarelyLelabo", "Vasarely × Le Labo"],
  ["vasarelyLemaire", "Vasarely × Lemaire"],
  ["vasarelyEtudes", "Vasarely × Études Studio"],
  ["vasarelySandro", "Vasarely × Sandro"],
  ["vasarelyClaudie", "Vasarely × Claudie Pierlot"],
  ["vasarelyBonparfumeur", "Vasarely × Bon Parfumeur"],
  ["vasarelyMerci", "Vasarely × Merci Paris"],
  ["vasarelyLouvreposterclub", "Vasarely × Louvre Poster Club"],
  ["vasarelyBuly", "Vasarely × Officine Universelle Buly"],
  ["vasarelyPaperclub", "Vasarely × Paperclub Paris"],
  ["vasarelyAlainmikli", "Vasarely × Alain Mikli"],
  ["vasarelyPapiertigre", "Vasarely × Papier Tigre"],
  ["vasarelyAstier", "Vasarely × Astier de Villatte"],
  ["vasarelyFragonard", "Vasarely × Fragonard Parfumeur"],
  ["vasarelyFragonard", "Vasarely × Fragonard Parfumeur"],
  ["vasarelyLion", "Vasarely × Lion"],
  ["vasarelyTiger", "Vasarely × Tiger"],
  ["vasarelyElephant", "Vasarely × Elephant"],
  ["vasarelyZebra", "Vasarely × Zebra"],
  ["vasarelyLeopard", "Vasarely × Leopard"],
  ["vasarelyCheetah", "Vasarely × Cheetah"],
  ["vasarelyGazelle", "Vasarely × Gazelle"],
  ["vasarelyRhino", "Vasarely × Rhino"],
  ["vasarelyHippo", "Vasarely × Hippo"],
  ["vasarelyPenguin", "Vasarely × Penguin"],
  ["vasarelyOwl", "Vasarely × Owl"],
  ["vasarelyEagle", "Vasarely × Eagle"],
  ["vasarelyFox", "Vasarely × Fox"],
  ["vasarelyWolf", "Vasarely × Wolf"],
  ["vasarelyBear", "Vasarely × Bear"],
  ["vasarelyKangaroo", "Vasarely × Kangaroo"],
  ["vasarelyKoala", "Vasarely × Koala"],
  ["vasarelyFlamingo", "Vasarely × Flamingo"],
  ["vasarelyWhale", "Vasarely × Whale"],
  ["vasarelyDolphin", "Vasarely × Dolphin"],
  ["vasarelyCapybara", "Vasarely × Capybara"],
  ["vasarelySuzanaiBobo", "Vasarely × Suzan AI (Bobo)"],
  ["vasarelySuzanai", "Vasarely × SUZAN AI Governance OS"],
];

// Export anonymized labels
export const modes: [string, string][] = originalModes.map(([k, _]) => [k, randomLabelFromKey(k)]);
